/**
 * The scroll position of each history entry, and its restore on Back (#595).
 *
 * The browser restores the position at once on Back, but a list page loads
 * its rows again after the navigation. At that moment the page is short, so
 * the browser can only go to the top (measured 2026-10-14: the tournament
 * list, 8,357 px, came back at 0). The app turns the browser restore off and
 * restores the position itself, when the page is tall enough.
 *
 * A position belongs to one history entry: the key of React Router plus the
 * URL. The positions stay in `sessionStorage`, so a reload restores the
 * position too. The first entry of each document in a tab has the key
 * `default`, so a new document (a typed URL, a link from another site)
 * forgets what an older document stored under that key.
 */

import { scrubUrl } from './telemetryScrub';
import { session } from './safeStorage';

const STORAGE_KEY = 'lmf:scroll-positions';
const MAX_ENTRIES = 100;
/** A restore stops after this time, as near to the position as the page allows. */
const RESTORE_TIMEOUT_MS = 5000;
/** An input of the person stops a restore: the person wins over the app. */
const USER_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
/** The prefix of the values of `useEntryState`, which follow the same rule. */
export const ENTRY_STATE_PREFIX = 'lmf:entry:';

/** True when a typed URL or a link loaded this document: not a reload, not Back or Forward. */
export const freshDocument = (() => {
  try {
    const type = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type;
    return type !== 'reload' && type !== 'back_forward';
  } catch {
    return true;
  }
})();

const positions = new Map<string, number>(load().filter(([entry]) => !(freshDocument && entry.startsWith('default|'))));
if (freshDocument) forgetDefaultEntryState();
let restoring = false;
/** The history entry of the page on screen. See `setEntryOnScreen`. */
let onScreen: string | null = null;

/** A blocked storage holds no old value: `keysWithPrefix` gives no key. */
function forgetDefaultEntryState(): void {
  for (const k of session.keysWithPrefix(`${ENTRY_STATE_PREFIX}default:`)) session.remove(k);
}

/** A blocked storage or a damaged value gives no position. */
function load(): Array<[string, number]> {
  const parsed = session.getJson<unknown>(STORAGE_KEY, []);
  return Array.isArray(parsed)
    ? parsed.filter((e): e is [string, number] => Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'number')
    : [];
}

/** Write the positions to `sessionStorage`. A blocked or full storage keeps them in memory only: the positions of this page view still work. */
export function persistPositions(): void {
  session.setJson(STORAGE_KEY, [...positions]);
}

/** The key of the current history entry. React Router keeps it in `history.state`. */
function currentEntry(): string {
  const key = (window.history.state as { key?: string } | null)?.key ?? 'default';
  return entryKey(key, window.location.pathname, window.location.search);
}

/** The key of an entry: the key of React Router and the URL. The store keeps
 *  no token and no search text: `scrubUrl` (lib/telemetryScrub.ts) blanks
 *  them, and the key of React Router keeps each entry apart. */
export function entryKey(locationKey: string, pathname: string, search: string): string {
  return `${locationKey}|${pathname}${scrubUrl(search)}`;
}

/**
 * The entry of the page that React committed. Back changes `history.state`
 * at once, but the old page stays on screen until the commit, so a scroll
 * event in that time belongs to the old entry, not to the entry in
 * `history.state`.
 */
export function setEntryOnScreen(entry: string): void {
  onScreen = entry;
}

/** Store the position of the entry on screen. Called on each scroll event. */
export function recordPosition(): void {
  if (restoring) return;
  const entry = onScreen ?? currentEntry();
  positions.delete(entry);
  positions.set(entry, Math.round(window.scrollY));
  if (positions.size > MAX_ENTRIES) positions.delete(positions.keys().next().value as string);
}

export function storedPosition(entry: string): number | null {
  return positions.get(entry) ?? null;
}

/** Scroll at once. The page has `scroll-behavior: smooth`, and an animation
 *  from the top stored the positions between the top and `y`. */
export function scrollAtOnce(top: number): void {
  window.scrollTo({ top, behavior: 'instant' });
}

const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;

/**
 * Scroll to `y` when the page is tall enough. The first try is in the next
 * frame, not at once: the page of the entry sets its filters and its count
 * in its layout effects, after the effect that calls this function. It tries
 * again each time the body changes its size (the rows arrive, a skeleton
 * goes). It stops at the position, or on an input of the person. After
 * RESTORE_TIMEOUT_MS it goes as near as the page allows: a list can have one
 * row fewer than before. Returns a function that stops it.
 */
export function restorePosition(y: number): () => void {
  let done = false;
  let observer: ResizeObserver | null = null;
  let timer = 0;
  const stop = () => {
    if (done) return;
    done = true;
    restoring = false;
    observer?.disconnect();
    window.clearTimeout(timer);
    for (const e of USER_EVENTS) window.removeEventListener(e, stop);
  };
  const attempt = () => {
    if (done) return;
    if (maxScroll() + 1 >= y) {
      stop();
      scrollAtOnce(y);
    }
  };
  restoring = true;
  for (const e of USER_EVENTS) window.addEventListener(e, stop, { passive: true });
  timer = window.setTimeout(() => {
    const max = maxScroll();
    if (max > 0) scrollAtOnce(Math.min(y, max));
    // The scroll event of that scroll comes before the next frame: it must
    // not store the nearer position over the real one, for a later Back.
    requestAnimationFrame(stop);
  }, RESTORE_TIMEOUT_MS);
  // A ResizeObserver calls `attempt` once when it starts, in the next frame.
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(attempt);
    observer.observe(document.body);
  } else {
    requestAnimationFrame(attempt);
  }
  return stop;
}
